import { useSetSheetTitleExtras, useSyncTitle } from "@/context/sheet";
import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import {
  BaseFormType,
  FormOptions,
  FormResult,
  useForm,
} from "@deadcow-enterprises/react-forms";
import { FormEvent, ReactNode } from "react";
import { Field } from "../field";
import { LoadingSpinner } from "../loading-spinner";
import { Button } from "../ui/button";
import { SheetFooter } from "../ui/sheet";

interface Field<T extends BaseFormType> {
  visible?: (form: FormResult<T, FormEvent<Element>>) => boolean;
  key: keyof T;
  hideErrors?: boolean;
  label: string | ReactNode | null;
  orientation?: "horizontal" | "vertical";
  input: (form: FormResult<T, FormEvent<Element>>, id: string) => ReactNode;
}

interface DomainUpsertProps<T extends BaseFormType> {
  title: string;
  buttonTranslation: string;
  formConfig: FormOptions<T>;
  onUpsert: (data: T) => Promise<void>;
  fields: Array<Field<T> | null>;
}
export function DomainUpsert<T extends BaseFormType>({
  title,
  buttonTranslation,
  formConfig,
  onUpsert,
  fields,
}: DomainUpsertProps<T>) {
  useSyncTitle(title);
  useSetSheetTitleExtras();
  const form = useForm(formConfig);

  return (
    <DomainForm
      form={form}
      fields={fields}
      buttonTranslation={buttonTranslation}
      onUpsert={onUpsert}
    />
  );
}

export function DomainForm<T extends BaseFormType>({
  form,
  buttonTranslation,
  fields,
  onUpsert,
}: {
  form: FormResult<T, FormEvent<Element>>;
  fields: Array<Field<T> | null>;
  buttonTranslation: string;
  onUpsert: (data: T) => Promise<void>;
}) {
  return (
    <UpsertForm fields={fields} form={form} onUpsert={onUpsert}>
      <SheetFooter>
        <Button type="submit" disabled={form.loading}>
          {form.loading ? <LoadingSpinner /> : <T tKey={buttonTranslation} />}
        </Button>
      </SheetFooter>
    </UpsertForm>
  );
}

export function UpsertForm<T extends BaseFormType>({
  children,
  fields,
  form,
  onUpsert,
  className,
}: {
  children?: ReactNode;
  form: FormResult<T, FormEvent<Element>>;
  fields: Array<Field<T> | null>;
  onUpsert: (data: T) => Promise<void>;
  className?: string;
}) {
  return (
    <form onSubmit={form.handleSubmit(onUpsert)}>
      <div className={cn("grid gap-4 py-4", className)}>
        {(
          fields.filter(
            (f) => Boolean(f) && (f?.visible?.(form) ?? true),
          ) as Array<Field<T>>
        ).map((field) => (
          <Field
            orientation={field.orientation}
            key={field.key as string}
            id={field.key.toString()}
            label={field.label}
            errors={field.hideErrors ? undefined : form.errors[field.key]}
          >
            {field.input(form, field.key.toString())}
          </Field>
        ))}
      </div>
      {children}
    </form>
  );
}
